import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { faSave, faLayerGroup, faArrowUp, faArrowDown, faChevronLeft, faSort, faChevronDown, faChevronRight, faCog } from '@fortawesome/free-solid-svg-icons';
import { PageContainerService, TimerService } from '../../services';
import { ProjectAccessArea, PROJECT_IDLE_TIMEOUT_MS } from '../../utilities/project-access-area';
import { GetReadinessFormDataCommandResponse, ReadinessAdminDataCommandResponse, ReadinessColumnDataDto, SaveReadinessAdminDataCommandRequest, SaveReadinessAdminDataCommandResponse } from '../../data/CommandServiceDtos.generated';
import { MatDialog } from '@angular/material/dialog';
import { ReadinessTrafficLightsSettingsComponent } from '../readiness-traffic-lights-settings/readiness-traffic-lights-settings.component';


@Component({
  standalone: false,
  selector: 'readiness-admin-form',
  templateUrl: './readiness-admin-form.component.html',
  styleUrls: ['./readiness-admin-form.component.scss'],
})
export class ReadinessAdminFormComponent implements OnInit, OnDestroy {
  faSave = faSave;
  faSort = faSort;
  faCog = faCog;
  faChevronLeft = faChevronLeft;
  faArrowDown = faArrowDown;
  faArrowUp = faArrowUp;
  faChevronDown = faChevronDown;
  faChevronRight = faChevronRight;
  faLayerGroup = faLayerGroup;
  projectId: number = 0;
  isAutoRouted: boolean = false;
  readinessFormData: ReadinessColumnDataDto[] = [];
  titleValue: string = "Readiness Form Settings";
  menuList: string[] = ['Dashboard', 'Users', 'Settings', 'Reports', 'Projects'];
  selectedMenu: string = '';
  isActive: boolean = false;
  isRequired: boolean = false;
  globalSortColumn: string = '';
  globalSortOrder: string = 'asc';
  groupedData: { sectionName: string, sectionOrder: number, data: ReadinessColumnDataDto[] }[] = [];
  sortOrderState: { [key: string]: 'asc' | 'desc' } = {};
  categoryToggle: { [key: string]: boolean } = {};
  draggedRowIndex: number | null = null;
  draggedSectionIndex: number | null = null;
  draggedRowSection: string | null = null;
  originalColumnOrder: number[] = [];
  originalColumnOrderMap: { [key: string]: number[] } = {};
  trafficLightRulesBySection: { [sectionName: string]: any[] } = {};


  openColorSettings(event: MouseEvent, sectionName: string) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ReadinessTrafficLightsSettingsComponent, {
      width: '50vw',
      // height: '50vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: { SectionName: sectionName, ProPakProjectId: this.projectId },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadAllTrafficLightRules();
    });
    console.log("Hello");


  }

  constructor(public pcs: PageContainerService, private route: ActivatedRoute, private dialog: MatDialog, public timerService: TimerService) { }

  ngOnInit() {
    this.projectId = +this.route.snapshot.paramMap.get('id');
    this.pcs.apiService.LogTimeIn({ ProjectId: this.projectId, AccessArea: ProjectAccessArea.ReadinessAdmin } as any, false, false);
    this.timerService.startIdleMonitor(PROJECT_IDLE_TIMEOUT_MS, () => {
      this.isAutoRouted = true;
      this.pcs.router.navigate(['/dashboard']);
    });
    this.GetReadinessAdminData();

  }

  ngOnDestroy() {
    this.timerService.stopIdleMonitor();
    this.pcs.apiService.LogTimeOut({ ProjectId: this.projectId, IsAutoRouted: this.isAutoRouted, TimeOut: this.timerService.lastActivity, AccessArea: ProjectAccessArea.ReadinessAdmin } as any, false, false);
  }

  async GetReadinessAdminData() {
    try {
      const response = await this.pcs.apiService.ReadinessAdminData({ ProPakProjectId: this.projectId, IncludeHiddenColumns: true });

      if (response && response.ReadinessTableData) {
        this.readinessFormData = response.ReadinessTableData;
        this.setColumnOrder();
      } else {
        console.error("Invalid API response:", response);
        this.readinessFormData = [];
      }
    } catch (error) {
      console.error("Error fetching readiness data:", error);
    }

    this.groupData();
    await this.loadAllTrafficLightRules();
  }

  groupData() {
    const grouped = this.readinessFormData.reduce((acc, item) => {
      let section = acc.find(g => g.sectionName === item.SectionName);
      if (!section) {
        section = { sectionName: item.SectionName, sectionOrder: item.SectionOrder, data: [] };
        acc.push(section);
        this.categoryToggle[item.SectionName] = false; // Default to collapse
      }
      section.data.push(item);
      return acc;
    }, []);

    // Sort sections by SectionOrder
    this.groupedData = grouped.sort((a, b) => a.sectionOrder - b.sectionOrder);

    // Sort data within each section by ColumnOrder
    this.groupedData.forEach(group => {
      group.data.sort((a, b) => a.ColumnOrder - b.ColumnOrder);
    });
  }

  toggleCategory(sectionName: string) {
    this.categoryToggle[sectionName] = !this.categoryToggle[sectionName];
  }


  async saveAllTableData() {
    let readinessTableData = {
      ReadinessTableData: this.readinessFormData,
      ProPakProjectId: this.projectId
    };

    let response = await this.saveReadinessTableData(readinessTableData);
    if (response) {
      await this.GetReadinessAdminData();
    }
  }

  async saveReadinessTableData(request: SaveReadinessAdminDataCommandRequest) {
    const response: SaveReadinessAdminDataCommandResponse = await this.pcs.apiService.SaveReadinessAdminData(request, true, false);
    return this.processSaveResponse(response);
  }

  processSaveResponse(response: any) {
    if (response.ReadinessTableData.length > 0) {
      void this.pcs.alert('Your changes have been saved successfully!', 'Saved', 'success');
      return true;
    }
    else {
      return false;
    }
  }

  onSectionDragStart(event: DragEvent, index: number) {
    this.draggedSectionIndex = index;
    event.dataTransfer!.effectAllowed = "move";
  }

  onSectionDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onSectionDrop(event: DragEvent, targetIndex: number) {
    event.preventDefault();

    if (this.draggedSectionIndex === null || this.draggedSectionIndex === targetIndex) {
      return;
    }
    if (this.draggedSectionIndex === 0 || targetIndex === 0) {
      return;
    }

    // Move section in groupedData array
    const draggedSection = this.groupedData.splice(this.draggedSectionIndex, 1)[0];
    this.groupedData.splice(targetIndex, 0, draggedSection);

    // Update SectionOrder in groupedData
    this.groupedData.forEach((group, index) => {
      group.sectionOrder = index + 1;
    });

    // Sync sectionOrder with readinessFormData
    this.updateReadinessFormData();

    this.draggedSectionIndex = null;
  }

  updateReadinessFormData() {
    this.groupedData.forEach(group => {
      this.readinessFormData.forEach(item => {
        if (item.SectionName === group.sectionName) {
          item.SectionOrder = group.sectionOrder;
        }
      });
    });

    // Ensure readinessFormData stays sorted
    this.readinessFormData.sort((a, b) => a.SectionOrder - b.SectionOrder);
  }


  onRowDragStart(event: DragEvent, rowIndex: number, sectionName: string) {
    this.draggedRowIndex = rowIndex;
    this.draggedRowSection = sectionName;
    event.dataTransfer!.effectAllowed = "move";
  }

  onRowDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onRowDropsss(event: DragEvent, targetRowIndex: number, sectionName: string) {
    event.preventDefault();

    const draggedElement = document.querySelector('.dragging');
    if (draggedElement) {
      draggedElement.classList.remove('dragging');
    }

    if (this.draggedRowIndex === null) return;

    // Find the correct section
    const section = this.groupedData.find(g => g.sectionName === sectionName);
    if (!section) return;

    const draggedRow = section.data[this.draggedRowIndex];

    // Remove from old position
    section.data.splice(this.draggedRowIndex, 1);

    // Insert at new position
    section.data.splice(targetRowIndex, 0, draggedRow);

    // Step 4: Update ColumnOrder for this section using originalColumnOrderMap
    const originalColumnOrder = this.originalColumnOrderMap[sectionName] || [];

    section.data.forEach((item, index) => {
      item.ColumnOrder = originalColumnOrder[index] || index + 1; // Assign new ColumnOrder
    });

    // Reset dragged row index
    this.draggedRowIndex = null;
  }

  onRowDrop(event: DragEvent, targetRowIndex: number, targetSectionName: string) {
    event.preventDefault();

    const draggedElement = document.querySelector('.dragging');
    if (draggedElement) {
      draggedElement.classList.remove('dragging');
    }

    if (this.draggedRowIndex === null || !this.draggedRowSection) return;

    // Find source and target sections
    const sourceSection = this.groupedData.find(g => g.sectionName === this.draggedRowSection);
    const targetSection = this.groupedData.find(g => g.sectionName === targetSectionName);

    if (!sourceSection || !targetSection) return;

    // Remove row from source section
    const [movedRow] = sourceSection.data.splice(this.draggedRowIndex, 1);

    // Update section details if moving to a new section
    if (this.draggedRowSection !== targetSectionName) {
      movedRow.SectionName = targetSectionName;
      movedRow.SectionOrder = targetSection.sectionOrder;
    }

    // Insert row into target section at the specified position
    targetSection.data.splice(targetRowIndex, 0, movedRow);

    // Update ColumnOrder in both sections
    this.updateColumnOrder(sourceSection);
    this.updateColumnOrder(targetSection);

    // Reset dragged row index and section
    this.draggedRowIndex = null;
    this.draggedRowSection = null;
  }

  // Helper function to update ColumnOrder in a section
  updateColumnOrder(section: { data: ReadinessColumnDataDto[]; sectionName: string }) {
    const originalColumnOrder = this.originalColumnOrderMap[section.sectionName] || [];
    section.data.forEach((item, index) => {
      item.ColumnOrder = originalColumnOrder[index] || index + 1;
    });
  }


  saveNewSettings() {
    const settings = {
      menu: this.selectedMenu,
      isActive: this.isActive,
      isRequired: this.isRequired
    };

    console.log('Saved Settings:', settings);
    void this.pcs.alert('Settings Saved Successfully!', 'Saved', 'success');
  }

  sortAllRows(sectionName: string, column: string) {
    // Generate a unique key for the section and column
    const sortKey = `${sectionName}-${column}`;

    // Toggle the sort order
    const currentSortOrder = this.sortOrderState[sortKey] || 'asc';
    const newSortOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';
    this.sortOrderState[sortKey] = newSortOrder;

    // Find the section by name
    const section = this.groupedData.find(group => group.sectionName === sectionName);
    if (section) {
      // Sort the data within the section based on the column and the new sort order
      section.data.sort((a, b) => {
        const valueA = a[column];
        const valueB = b[column];

        // Compare values based on the current sort order (ascending or descending)
        if (newSortOrder === 'asc') {
          return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
        } else {
          return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
        }
      });
    }
  }

  get sortOrderForIsVisible() {
    return (sectionName: string) => {
      const key = `${sectionName}-IsVisible`;
      return this.sortOrderState[key] ? this.sortOrderState[key] : null;
    };
  }

  get sortOrderForIsRequired() {
    return (sectionName: string) => {
      const key = `${sectionName}-IsRequired`;
      return this.sortOrderState[key] ? this.sortOrderState[key] : null;
    };
  }

  setColumnOrder() {
    // Step 1: Sort readinessFormData by SectionOrder and SectionName
    this.readinessFormData.sort((a, b) => {
      if (a.SectionOrder !== b.SectionOrder) {
        return a.SectionOrder - b.SectionOrder; // Sort by SectionOrder
      }
      return a.SectionName.localeCompare(b.SectionName); // Sort by SectionName alphabetically
    });

    const sectionGroups: { [key: string]: ReadinessColumnDataDto[] } = {};

    // Step 2: Group data by SectionName
    this.readinessFormData.forEach(item => {
      if (!sectionGroups[item.SectionName]) {
        sectionGroups[item.SectionName] = [];
      }
      sectionGroups[item.SectionName].push(item);
    });

    // Step 3: Assign ColumnOrder within each section and store in `originalColumnOrderMap`
    this.originalColumnOrderMap = {}; // Reset map

    Object.entries(sectionGroups).forEach(([sectionName, sectionData]) => {
      const allZero = sectionData.every(row => row.ColumnOrder === 0);

      this.originalColumnOrderMap[sectionName] = []; // Initialize array for each section

      sectionData.forEach((item, index) => {
        item.ColumnOrder = allZero ? index + 1 : item.ColumnOrder;
        this.originalColumnOrderMap[sectionName].push(item.ColumnOrder);
      });
    });
  }

  async loadAllTrafficLightRules() {
    try {
      const response = await this.pcs.apiService.GetReadinessTrafficLightRule({
        SectionName: null,
        ProPakProjectId: this.projectId
      });

      this.trafficLightRulesBySection = {};
      if (response && response.ReadinessTrafficLightData) {
        const allRules = response.ReadinessTrafficLightData;

        allRules.forEach(rule => {
          const section = rule.SectionName;
          if (!this.trafficLightRulesBySection[section]) {
            this.trafficLightRulesBySection[section] = [];
          }
          this.trafficLightRulesBySection[section].push(rule);
        });

      } else {
        console.error("Invalid API response:", response);
      }
    } catch (error) {
      console.error("Error fetching traffic lights data:", error);
    }
  }

}
